import { useEffect, useRef } from "react";

interface AdSenseUnitProps {
  className?: string;
}

declare global {
  interface Window {
    adsbygoogle: unknown[];
  }
}

export const AdSenseUnit = ({ className = "" }: AdSenseUnitProps) => {
  const adRef = useRef<HTMLModElement>(null);
  const isInitialized = useRef(false);

  // Check if we're in a production environment
  const isProduction = typeof window !== 'undefined' && 
    !window.location.hostname.includes('localhost') &&
    !window.location.hostname.includes('127.0.0.1') &&
    !window.location.hostname.includes('lovableproject') &&
    !window.location.hostname.includes('lovable.app');

  useEffect(() => {
    // Skip AdSense in non-production environments
    if (!isProduction) {
      console.log('[AdSense] Skipped - not in production environment');
      return;
    }

    const el = adRef.current;
    if (!el || isInitialized.current) return;

    // If another AdSense push already filled this <ins>, don't push again.
    const isAlreadyFilled =
      el.getAttribute("data-adsbygoogle-status") === "done" ||
      el.innerHTML.trim().length > 0;

    if (isAlreadyFilled) {
      isInitialized.current = true;
      return;
    }

    // Delay AdSense initialization to avoid blocking render
    const timer = window.setTimeout(() => {
      try {
        // Check if adsbygoogle is available
        if (typeof window.adsbygoogle !== 'undefined') {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        }
      } catch (e) {
        // Silently handle AdSense errors to prevent app crashes
        console.log('[AdSense] Push skipped:', e);
      }
    }, 100);

    isInitialized.current = true;

    return () => {
      window.clearTimeout(timer);
    };
  }, [isProduction]);

  // Don't render anything in non-production environments
  if (!isProduction) {
    return null;
  }

  return (
    <div className={`w-full overflow-hidden ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-card/50 rounded-xl p-4 shadow-sm border border-border/50">
          <ins
            ref={adRef}
            className="adsbygoogle"
            style={{ display: "block" }}
            data-ad-client="ca-pub-4586620936286123"
            data-ad-slot="auto"
            data-ad-format="auto"
            data-full-width-responsive="true"
          />
        </div>
      </div>
    </div>
  );
};
